// /home/labdoo/zitech-nexus/frontend/src/pages/admin/ServicesList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import './ServicesList.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Toggle: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="5" width="22" height="14" rx="7" />
      <circle cx="16" cy="12" r="3" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sl-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Service: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

const ServicesList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<any[]>([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await adminAPI.getAllServices();
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await adminAPI.deleteService(id);
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
        alert('Failed to delete service');
      }
    }
  };

  const toggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await adminAPI.updateService(id, { is_active: !currentStatus });
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      alert('Failed to update service status');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? '#10b981' : '#ef4444';
  };

  const getStatusBackground = (isActive: boolean) => {
    return isActive ? '#d1fae5' : '#fee2e2';
  };

  const getStatusLabel = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  const counts = {
    total: services.length,
    active: services.filter(s => s.is_active).length,
    inactive: services.filter(s => !s.is_active).length,
  };

  if (loading) {
    return (
      <div className="sl-loading-container">
        <div className="sl-spinner">
          <Icons.Spinner />
        </div>
        <p className="sl-loading-text">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="sl-container">
      {/* Header */}
      <div className="sl-header">
        <div className="sl-header-left">
          <h1 className="sl-title">Service Management</h1>
          <p className="sl-subtitle">Manage your service offerings and their availability</p>
        </div>
        <button 
          className="sl-btn-primary"
          onClick={() => navigate('/dashboard/admin/services/create')}
        >
          <Icons.Plus />
          Add New Service
        </button>
      </div>

      {/* Stats Bar */}
      <div className="sl-stats-bar">
        <div className="sl-stat-item">
          <span className="sl-stat-label">Total Services</span>
          <span className="sl-stat-value">{counts.total}</span>
        </div>
        <div className="sl-stat-divider"></div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Active</span>
          <span className="sl-stat-value sl-stat-active">{counts.active}</span>
        </div>
        <div className="sl-stat-divider"></div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Inactive</span>
          <span className="sl-stat-value sl-stat-inactive">{counts.inactive}</span>
        </div>
      </div>

      {/* Table */}
      <div className="sl-table-wrapper">
        <table className="sl-table">
          <thead>
            <tr>
              <th className="sl-col-name">Name</th>
              <th className="sl-col-desc">Description</th>
              <th className="sl-col-status">Status</th>
              <th className="sl-col-created">Created</th>
              <th className="sl-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 ? (
              <tr>
                <td colSpan={5} className="sl-empty-state">
                  <Icons.Empty />
                  <p className="sl-empty-text">No services found</p>
                  <p className="sl-empty-subtext">Create your first service to get started</p>
                </td>
              </tr>
            ) : (
              services.map((service) => (
                <tr key={service.id} className="sl-table-row">
                  <td className="sl-col-name">
                    <span className="sl-service-name">
                      <Icons.Service />
                      {service.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                    </span>
                  </td>
                  <td className="sl-col-desc">
                    <span className="sl-description" title={service.description}>
                      {service.description.length > 100 
                        ? service.description.substring(0, 100) + '...' 
                        : service.description}
                    </span>
                  </td>
                  <td className="sl-col-status">
                    <span 
                      className="sl-status-badge"
                      style={{
                        background: getStatusBackground(service.is_active),
                        color: getStatusColor(service.is_active)
                      }}
                    >
                      <Icons.StatusDot />
                      {getStatusLabel(service.is_active)}
                    </span>
                  </td>
                  <td className="sl-col-created">
                    <span className="sl-date-text">
                      <Icons.Calendar />
                      {new Date(service.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="sl-col-actions">
                    <div className="sl-actions">
                      <button 
                        className="sl-action-btn sl-action-edit"
                        onClick={() => navigate(`/dashboard/admin/services/${service.id}/edit`)}
                        title="Edit Service"
                      >
                        <Icons.Edit />
                      </button>
                      <button 
                        className={`sl-action-btn ${service.is_active ? 'sl-action-deactivate' : 'sl-action-activate'}`}
                        onClick={() => toggleStatus(service.id, service.is_active)}
                        title={service.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <Icons.Toggle />
                        {service.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        className="sl-action-btn sl-action-delete"
                        onClick={() => handleDelete(service.id)}
                        title="Delete Service"
                      >
                        <Icons.Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServicesList;